import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Names the history entry a page claims while an embedded scheduler is on
 * screen. The value changes with every claim so the entry underneath can never
 * repeat the one on top and hide a Back press.
 */
const FRAME_PARAM = "frame";

/** How often the page looks for an entry the embedded scheduler added. */
const FRAME_POLL_MS = 200;

/**
 * How many of this tab's history entries belong to an embedded frame.
 *
 * The Navigation API lists only the entries of the frame it is read from, so
 * whatever the tab counts beyond that list is the embed's — a count the page's
 * own entries cancel out of, and one that survives the embed dropping entries,
 * which the tab's history length alone does not. Without that API the length
 * is the only clue there is, and only the growth in it reads true.
 */
const framedEntryCount = (): number => {
  const { navigation } = window as Window & {
    navigation?: { entries: () => unknown[] };
  };
  return navigation
    ? window.history.length - navigation.entries().length
    : window.history.length;
};

const readClaim = (search: string): number =>
  Number(new URLSearchParams(search).get(FRAME_PARAM) ?? 0);

/**
 * Keeps Back inside the app on a page that embeds a scheduler in an iframe.
 *
 * The scheduler navigates itself — once while starting up, and again as the
 * visitor moves through its own steps — and every one of those navigations
 * adds an entry to this tab's history while leaving the page's URL untouched.
 * Back off one of them therefore rewinds the frame to a scheduling step the
 * visitor already left, stranding them in someone else's flow instead of
 * returning them to ours.
 *
 * So the page claims the entry on top again after each one the frame adds,
 * which puts the next Back press back in its hands, and keeps count of what it
 * is standing on. Leaving — by the page's own Back control or by the browser's
 * — steps over that whole pile in one traversal, landing the visitor on the
 * entry they were on before the scheduler opened.
 *
 * @param active Whether the embedded scheduler is on screen.
 * @returns The handler for the page's own Back control.
 */
export const useEmbeddedFrameBack = (active: boolean): (() => void) => {
  const navigate = useNavigate();
  const location = useLocation();

  const claim = readClaim(location.search);

  // The claim the page last pushed, and whether the router has caught up with
  // it — a navigation applies a render after the claim is made.
  const claimed = useRef(0);
  const claimReached = useRef(false);
  // Claims are numbered across visits, so a later one can't repeat a value
  // still sitting in history from an earlier one.
  const claimsMade = useRef(0);
  // Entries stacked above the one the scheduler opened on: the frame's, and
  // the ones claimed back on top of them.
  const depth = useRef(0);
  // A traversal takes a moment to land. Claiming another entry in the middle
  // of one would put a new entry in the way of where it is headed.
  const leaving = useRef(false);

  const leave = useCallback(
    (steps: number) => {
      claimReached.current = false;
      leaving.current = true;
      navigate(-steps);
    },
    [navigate]
  );

  // Claiming in answer to the frame rather than on a timer of its own is what
  // keeps this from filling the tab's history until the browser's entry cap
  // froze it and Back belonged to the frame again from then on.
  useEffect(() => {
    if (!active) return undefined;

    claimed.current = readClaim(window.location.search);
    claimReached.current = false;
    leaving.current = false;
    depth.current = 0;

    let counted = framedEntryCount();
    let claiming = false;

    const interval = window.setInterval(() => {
      if (leaving.current) return;

      // A claim of the page's own lands in the tab's history too. Re-read the
      // count once it has, so it can't read as another frame navigation.
      if (claiming) {
        counted = framedEntryCount();
        claiming = false;
        return;
      }

      const count = framedEntryCount();
      if (count <= counted) return;

      depth.current += count - counted + 1;
      counted = count;
      claiming = true;

      const next = claimsMade.current + 1;
      claimsMade.current = next;
      claimed.current = next;
      claimReached.current = false;

      const search = new URLSearchParams(window.location.search);
      search.set(FRAME_PARAM, String(next));
      navigate({ search: `?${search.toString()}` });
    }, FRAME_POLL_MS);

    return () => window.clearInterval(interval);
  }, [active, navigate]);

  // Landing off the claimed entry means the visitor pressed Back and is now on
  // one of the frame's entries, with nothing of ours on screen. Only a claim
  // the router reached and then left counts as that.
  useEffect(() => {
    if (!active) return;
    if (claim === claimed.current) {
      claimReached.current = true;
      return;
    }
    if (claimReached.current && depth.current > 0) {
      leave(depth.current);
    }
  }, [active, claim, leave]);

  // The page's own control is pressed from the entry on top, so the whole pile
  // stands between the visitor and the screen that sent them here.
  return useCallback(() => leave(depth.current + 1), [leave]);
};

export default useEmbeddedFrameBack;
